import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Copy, Check, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { EMAIL, API_BASE, TRADES } from "@/lib/data";
import { FadeUp, MaskLine } from "@/components/Reveal";

const NEXT_STEPS = [
    { n: "01", t: "You describe the job", d: "Location, property type, what needs doing." },
    { n: "02", t: "We scope and quote", d: "A clear written scope and a transparent price." },
    { n: "03", t: "We schedule and deliver", d: "Work done to code, with documentation at sign-off." },
];

export default function Contact() {
    const [searchParams] = useSearchParams();
    const preselected = searchParams.get("trade");
    const initialTrade = preselected
        ? `${preselected} — ${TRADES.find((t) => t.code === preselected)?.title || ""}`
        : `${TRADES[0].code} — ${TRADES[0].title}`;

    const [copied, setCopied] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        trade: initialTrade,
        message: "",
    });
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (preselected) {
            const t = TRADES.find((x) => x.code === preselected);
            if (t) setForm((f) => ({ ...f, trade: `${t.code} — ${t.title}` }));
        }
    }, [preselected]);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const copyEmail = async () => {
        try {
            await navigator.clipboard.writeText(EMAIL);
            setCopied(true);
            toast.success("Email copied to clipboard");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Could not copy — the address is david@servicesprestations.com");
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            await axios.post(`${API_BASE}/contact`, {
                name: form.name,
                email: form.email,
                phone: form.phone,
                trade: form.trade,
                message: form.message,
            });
            toast.success("Message sent — we'll get back to you by email.");
            setForm((f) => ({ ...f, name: "", email: "", phone: "", message: "" }));
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Could not send. Please email us directly.",
            );
        } finally {
            setSending(false);
        }
    };

    return (
        <section className="relative overflow-hidden pb-24 pt-40 md:pt-48">
            <div
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(ellipse 55% 45% at 80% 10%, rgba(245,158,11,0.1), transparent 65%)",
                }}
            />
            <div className="container-x relative">
                <p className="eyebrow">Contact</p>
                <h1 className="mt-6 font-display text-5xl font-extrabold tracking-tight text-white sm:text-6xl lg:text-7xl">
                    <MaskLine>Tell us what</MaskLine>
                    <MaskLine delay={0.15}>
                        <span className="text-amber-400">needs work.</span>
                    </MaskLine>
                </h1>

                <div className="mt-16 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.2fr] lg:gap-16">
                    <div>
                        <FadeUp>
                            <div className="rounded-2xl border border-white/10 bg-panel/50 p-7 md:p-9">
                                <p className="field-label">Write to us</p>
                                <a
                                    data-testid="contact-email-link"
                                    href={`mailto:${EMAIL}`}
                                    className="mt-2 block break-all font-mono text-lg font-medium text-amber-400 transition-colors hover:text-amber-300 md:text-2xl"
                                >
                                    {EMAIL}
                                </a>
                                <button
                                    data-testid="contact-copy-email-button"
                                    onClick={copyEmail}
                                    className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-display text-xs font-semibold text-white transition-all duration-300 hover:border-amber-400/60 hover:text-amber-400"
                                >
                                    {copied ? <Check size={14} /> : <Copy size={14} />}
                                    {copied ? "Copied" : "Copy address"}
                                </button>
                                <p className="mt-6 text-sm leading-relaxed text-slate-400">
                                    For quotes, interventions and maintenance
                                    requests — include your location and the type
                                    of work, and we reply by email.
                                </p>
                            </div>
                        </FadeUp>

                        <FadeUp delay={0.1}>
                            <div className="mt-6 rounded-2xl border border-white/10 p-7 md:p-9">
                                <p className="field-label">What happens next</p>
                                <div className="mt-2">
                                    {NEXT_STEPS.map((s) => (
                                        <div
                                            key={s.n}
                                            className="flex items-baseline gap-4 border-b border-white/5 py-4 last:border-0"
                                        >
                                            <span className="font-mono text-xs text-amber-400">{s.n}</span>
                                            <div>
                                                <p className="font-display text-sm font-semibold text-white">{s.t}</p>
                                                <p className="mt-1 text-xs text-slate-400">{s.d}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </FadeUp>
                    </div>

                    <FadeUp delay={0.15}>
                        <form
                            onSubmit={submit}
                            data-testid="contact-form"
                            className="rounded-2xl border border-white/10 bg-panel/50 p-7 md:p-9"
                        >
                            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                <div>
                                    <label className="field-label" htmlFor="c-name">Name *</label>
                                    <input
                                        id="c-name"
                                        data-testid="contact-form-name-input"
                                        required
                                        minLength={2}
                                        value={form.name}
                                        onChange={set("name")}
                                        placeholder="Your name"
                                        className="field-input"
                                    />
                                </div>
                                <div>
                                    <label className="field-label" htmlFor="c-email">Email *</label>
                                    <input
                                        id="c-email"
                                        data-testid="contact-form-email-input"
                                        type="email"
                                        required
                                        value={form.email}
                                        onChange={set("email")}
                                        placeholder="you@example.com"
                                        className="field-input"
                                    />
                                </div>
                            </div>
                            <div className="mt-5">
                                <label className="field-label" htmlFor="c-phone">Phone (optional)</label>
                                <input
                                    id="c-phone"
                                    data-testid="contact-form-phone-input"
                                    value={form.phone}
                                    onChange={set("phone")}
                                    placeholder="+00 000 000 000"
                                    className="field-input"
                                />
                            </div>
                            <div className="mt-5">
                                <label className="field-label" htmlFor="c-trade">Trade *</label>
                                <select
                                    id="c-trade"
                                    data-testid="contact-form-trade-select"
                                    value={form.trade}
                                    onChange={set("trade")}
                                    className="field-input appearance-none"
                                >
                                    {TRADES.map((t) => (
                                        <option
                                            key={t.code}
                                            value={`${t.code} — ${t.title}`}
                                            className="bg-[#131B2A]"
                                        >
                                            {t.code} — {t.title}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="mt-5">
                                <label className="field-label" htmlFor="c-message">Message *</label>
                                <textarea
                                    id="c-message"
                                    data-testid="contact-form-message-textarea"
                                    required
                                    minLength={5}
                                    rows={5}
                                    value={form.message}
                                    onChange={set("message")}
                                    placeholder="Describe the job — location, property type, what needs doing."
                                    className="field-input resize-none"
                                />
                            </div>
                            <button
                                type="submit"
                                data-testid="contact-form-submit-button"
                                disabled={sending}
                                className="btn-primary mt-7 w-full disabled:opacity-60"
                            >
                                {sending ? (
                                    "Sending…"
                                ) : (
                                    <>
                                        <Mail size={16} />
                                        Send message
                                    </>
                                )}
                            </button>
                            <p className="mt-4 flex items-center justify-center gap-1.5 text-center font-mono text-[11px] text-slate-500">
                                Or write directly to {EMAIL}
                                <ArrowRight size={11} />
                            </p>
                        </form>
                    </FadeUp>
                </div>
            </div>
        </section>
    );
}
